import { get } from 'lodash';

import { FIELD_PATHS, FIELD_VALUES, INCOME_TYPE } from '@funnels/constants';
import { FormValuesFunnelOne, Income } from '@funnels/types';

export const generateFunnelSchemaData = (funnelData: FormValuesFunnelOne) => {
  const cashoutAmount = get(funnelData, FIELD_PATHS.APPLICATION_CASHOUT_AMOUNT(0));
  const consolidateAmount = get(funnelData, FIELD_PATHS.APPLICATION_CONSOLIDATE_AMOUNT(0));
  const deposit = get(funnelData, FIELD_PATHS.APPLICATION_CONTRIBUTION_AMOUNT(0, 0));
  const desiredFeatures = get(funnelData, FIELD_PATHS.APPLICATION_DESIRED_FEATURES(0));
  const employmentType = get(funnelData, FIELD_PATHS.EMPLOYMENT_EMPLOYMENT_TYPE(0));
  const hasSelfEmploymentProof = get(funnelData, FIELD_PATHS.APPLICANT_HAS_SELF_EMPLOYMENT_PROOF(0));
  const loanValue = get(funnelData, FIELD_PATHS.LIABILITY_OUTSTANDING_BALANCE(0));
  const paymentType = get(funnelData, FIELD_PATHS.APPLICATION_PAYMENT_TYPE(0), FIELD_VALUES.PRINCIPAL_AND_INTEREST);
  const purpose = get(funnelData, FIELD_PATHS.APPLICATION_PRIMARY_PURPOSE(0));
  const propertyUse = get(funnelData, FIELD_PATHS.ASSET_PRIMARY_PURPOSE(0));
  const purchaseSituation = get(funnelData, FIELD_PATHS.APPLICATION_PURCHASE_SITUATION(0));
  // Array because the leads service expects this to be an array - legacy from lendi app
  const refinancingReasons = get(funnelData, FIELD_PATHS.APPLICATION_REFINANCING_REASONS(0));
  const securityValue = get(funnelData, FIELD_PATHS.ASSET_ESTIMATED_VALUE(0));
  const urgency = get(funnelData, FIELD_PATHS.APPLICATION_URGENCY(0));
  const buildPriceAmount = get(funnelData, FIELD_PATHS.ASSET_BUILD_PRICE_AMOUNT(0));
  //https://lendi-aus.slack.com/archives/CH58NUUFR/p1652080010150149
  const annualInterestRate = get(funnelData, FIELD_PATHS.LIABILITY_INTEREST_RATE(0));
  const financialInstitution = get(funnelData, FIELD_PATHS.LIABILITY_FINANCIAL_INSTITUTION(0));

  //SE v2 incomes
  const primaryEmploymentCompanyClientId = get(funnelData, FIELD_PATHS.EMPLOYMENT_COMPANY_CLIENT_ID(0));
  const hasSEV2DocValue: boolean = get(funnelData, FIELD_PATHS.INCOMES(), []).some(
    (income: Income) =>
      [INCOME_TYPE.SELF_EMPLOYED_BUSINESS_INCOME_FULL_DOC, INCOME_TYPE.SELF_EMPLOYED_BUSINESS_INCOME_LOW_DOC].includes(
        get({ incomes: [income] }, FIELD_PATHS.INCOME_TYPE(0))
      ) && get({ incomes: [income] }, FIELD_PATHS.INCOME_COMPANY_CLIENT_ID(0)) === primaryEmploymentCompanyClientId
  );

  const isSEV2FullDoc: boolean = get(funnelData, FIELD_PATHS.INCOMES(), []).some(
    (income: Income) =>
      get({ incomes: [income] }, FIELD_PATHS.INCOME_TYPE(0)) === INCOME_TYPE.SELF_EMPLOYED_BUSINESS_INCOME_FULL_DOC &&
      get({ incomes: [income] }, FIELD_PATHS.INCOME_COMPANY_CLIENT_ID(0)) === primaryEmploymentCompanyClientId
  );
  const eligibleForFHOG = get(funnelData, FIELD_PATHS.APPLICATION_ELIGIBLE_FOR_FHOG(0));

  // the FunnelDataV2 from @lendi/lead-client package is out of date
  // the correct type can be found here: https://app.swaggerhub.com/apis/lendi-api/leads-api/1.2.0#/
  // just search for FunnelDataV2 on the swagger page above
  return {
    cashoutAmount,
    consolidateAmount,
    deposit,
    desiredFeatures,
    employmentType,
    hasSelfEmploymentProof:
      hasSelfEmploymentProof !== undefined ? hasSelfEmploymentProof : hasSEV2DocValue ? isSEV2FullDoc : undefined,
    loanValue,
    paymentType,
    purpose,
    propertyUse,
    purchaseSituation,
    refinancingReasons,
    securityValue,
    urgency,
    buildPriceAmount,
    annualInterestRate,
    financialInstitution,
    eligibleForFHOG,
  };
};

export const generateBorrowingPowerSchemaData = (funnelData: FormValuesFunnelOne) => {
  const dependants = get(funnelData, FIELD_PATHS.APPLICATION_NUMBER_OF_DEPENDANTS(0), 0);
  const estimatedSettlementDate = get(funnelData, FIELD_PATHS.ASSET_ESTIMATED_SETTLEMENT_DATE(0));
  const monthlyExpenses = get(funnelData, FIELD_PATHS.EXPENSES_EXPENSE_AMOUNT(0, 0));
  const numberOfBorrowers = get(funnelData, FIELD_PATHS.APPLICANTS(), []).length;
  const purchaseSituation = get(funnelData, FIELD_PATHS.APPLICATION_PURCHASE_SITUATION(0));
  const yearlyIncome = get(funnelData, FIELD_PATHS.APPLICANT_GROSS_SALARY_AMOUNT(0));
  const eligibleForFHOG = get(funnelData, FIELD_PATHS.APPLICATION_ELIGIBLE_FOR_FHOG(0));
  const propertyUse = get(funnelData, FIELD_PATHS.ASSET_PRIMARY_PURPOSE(0));

  return {
    dependants,
    dependent: dependants,
    estimatedSettlementDate,
    monthlyExpenses,
    numberOfBorrowers,
    purchaseSituation,
    yearlyIncome,
    purchaseIntent: undefined,
    eligibleForFHOG,
    propertyUse,
  };
};
